import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { isSupportTicketsEnabled } from '../../../lib/support/supportContact';
import SupportContactCard from '../../../components/SupportContactCard/SupportContactCard';

interface FaqItem {
    /** Translation keys (i18n/owner/shell.ts). */
    question: string;
    answer: string;
}

const CONTACT_SUPPORT_QUESTION = 'help.o.faq.contact.q';

// O3 (SO-38) — FAQ copy through t(), pointing at where things live today
// (My Business is the one home for branches, services, staff and hours — SO-29).
const faqs: FaqItem[] = [
    { question: 'help.o.faq.branch.q', answer: 'help.o.faq.branch.a' },
    { question: 'help.o.faq.appointments.q', answer: 'help.o.faq.appointments.a' },
    { question: 'help.o.faq.hours.q', answer: 'help.o.faq.hours.a' },
    { question: 'help.o.faq.services.q', answer: 'help.o.faq.services.a' },
    { question: CONTACT_SUPPORT_QUESTION, answer: 'help.o.faq.contact.a' },
    { question: 'help.o.faq.password.q', answer: 'help.o.faq.password.a' },
];

const Help: React.FC = () => {
    const { t } = useLanguage();
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    // AB#2133 — tickets proxy to Zammad (OFF for the pilot): while the flag is
    // off, point owners at the Support page's contact card, not at a ticket.
    const ticketsEnabled = isSupportTicketsEnabled();
    const answerFor = (faq: FaqItem): string =>
        !ticketsEnabled && faq.question === CONTACT_SUPPORT_QUESTION
            ? t('help.faq.contactSupport.pilotAnswer')
            : t(faq.answer);

    const toggleFaq = (index: number) => {
        setExpandedIndex(prev => (prev === index ? null : index));
    };

    return (
        <div style={{ padding: '20px', maxWidth: 800 }}>
            <h1 className='xH1'>{t('nav.help')}</h1>

            <div style={{ marginTop: 24 }}>
                <h2 style={{ fontSize: '1.2rem', marginBottom: 16 }}>{t('help.o.faqTitle')}</h2>
                {faqs.map((faq, index) => (
                    <div
                        key={index}
                        style={{
                            border: '1px solid var(--border-subtle)',
                            borderRadius: 8,
                            marginBottom: 8,
                            overflow: 'hidden',
                        }}
                    >
                        <button
                            type="button"
                            aria-expanded={expandedIndex === index}
                            onClick={() => toggleFaq(index)}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                padding: '14px 16px',
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                textAlign: 'start',
                                color: 'inherit',
                            }}
                        >
                            {t(faq.question)}
                            <span style={{ fontSize: '1.2rem', marginInlineStart: 8 }} aria-hidden="true">
                                {expandedIndex === index ? '\u2212' : '+'}
                            </span>
                        </button>
                        {expandedIndex === index && (
                            <div style={{ padding: '0 16px 14px', color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                                {answerFor(faq)}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div style={{ marginTop: 32 }}>
                <h2 style={{ fontSize: '1.2rem', marginBottom: 12 }}>{t('help.o.needMoreTitle')}</h2>
                {ticketsEnabled ? (
                    <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        {t('help.o.needMoreTickets')}
                    </p>
                ) : (
                    <>
                        {/* SO-36 — the contact itself, right here: Help no longer sends
                            the owner to the Support page just to find it (dead-end loop). */}
                        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>
                            {t('help.needMoreHelp.pilotBody')}
                        </p>
                        <SupportContactCard />
                    </>
                )}
            </div>
        </div>
    );
};

export default Help;
